import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthService } from '@/services/authService';
import type { User, UserProfile, LoginRequest, RegisterRequest } from '@/types';

interface AuthState {
  // 状态
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;

  // 操作
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  bindPhone: (phone: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  updateProfile: (userData: Partial<UserProfile>) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
      error: null,

      // 登录
      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await AuthService.login(credentials);
          
          set({
            isAuthenticated: true,
            user: response.user,
            token: response.token,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            isLoading: false,
            error: error.message || '登录失败',
          });
          throw error;
        }
      },

      // 注册
      register: async (userData: RegisterRequest) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await AuthService.register(userData);
          
          set({
            isAuthenticated: true,
            user: response.user,
            token: response.token,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            isLoading: false,
            error: error.message || '注册失败',
          });
          throw error;
        }
      },

      bindPhone: async (phone: string, code: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await AuthService.bindPhone(phone, code);

          set({
            isAuthenticated: true,
            user: response.user,
            token: response.token,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || '绑定手机号失败',
          });
          throw error;
        }
      },

      // 登出
      logout: async () => {
        try {
          set({ isLoading: true });
          
          await AuthService.logout();
          
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // 即使服务器登出失败，也要清除本地状态
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            isLoading: false,
            error: null,
          });
          console.warn('Logout error:', error);
        }
      },

      // 刷新token
      refreshToken: async () => {
        try {
          const response = await AuthService.refreshToken();
          
          set({
            isAuthenticated: true,
            user: response.user,
            token: response.token,
            error: null,
          });
        } catch (error: any) {
          // token刷新失败，清除认证状态
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            error: error.message || 'Token刷新失败',
          });
          throw error;
        }
      },

      // 获取当前用户信息
      getCurrentUser: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const userProfile = await AuthService.getCurrentUser();
          
          set({
            user: userProfile,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || '获取用户信息失败',
          });
          throw error;
        }
      },

      // 更新用户资料
      updateProfile: async (userData: Partial<UserProfile>) => {
        try {
          set({ isLoading: true, error: null });
          
          const updatedUser = await AuthService.updateProfile(userData);
          
          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || '更新用户资料失败',
          });
          throw error;
        }
      },

      // 清除错误
      clearError: () => {
        set({ error: null });
      },

      // 设置加载状态
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // 初始化认证状态
      initializeAuth: () => {
        // 首先检查URL中是否有token参数（小程序/APP WebView嵌入场景）
        const tokenFromUrl = AuthService.initializeFromUrlToken();

        if (tokenFromUrl) {
          // 从URL获取了token，异步验证并获取用户信息
          set({ isLoading: true });
          AuthService.validateAndSyncUser().then((user) => {
            if (user) {
              set({
                isAuthenticated: true,
                user: user,
                token: AuthService.getLocalAuthToken(),
                isLoading: false,
              });
            } else {
              set({
                isAuthenticated: false,
                user: null,
                token: null,
                isLoading: false,
              });
            }
          });
          return;
        }

        // 常规初始化：从localStorage读取
        const token = AuthService.getLocalAuthToken();
        const userInfo = AuthService.getLocalUserInfo();

        if (token && userInfo) {
          set({
            isAuthenticated: true,
            user: userInfo,
            token: token,
            isLoading: false,
          });
        } else {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
      }),
    }
  )
);

// 选择器函数
export const useAuth = () => {
  const store = useAuthStore();
  return {
    isAuthenticated: store.isAuthenticated,
    user: store.user,
    token: store.token,
    isLoading: store.isLoading,
    error: store.error,
  };
};

export const useAuthActions = () => {
  const store = useAuthStore();
  return {
    login: store.login,
    register: store.register,
    bindPhone: store.bindPhone,
    logout: store.logout,
    refreshToken: store.refreshToken,
    getCurrentUser: store.getCurrentUser,
    updateProfile: store.updateProfile,
    clearError: store.clearError,
    setLoading: store.setLoading,
    initializeAuth: store.initializeAuth,
  };
};
